import {
  faArrowLeft,
  faBook,
  faCalendar,
  faIndianRupeeSign,
  faLayerGroup,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { ExamGroupService } from "Services/examGrouo";
import { ExamServices } from "Services/examServices";

interface ExamGroup {
  _id: string;
  name: string;
  fees?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ExamItem {
  _id: string;
  examName: string;
}

export default function ExamGroupDetails() {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState<ExamGroup | null>(null);
  const [exams, setExams] = useState<ExamItem[]>([]);
  const [allExams, setAllExams] = useState<ExamItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedExam, setSelectedExam] = useState<{ value: string; label: string } | null>(null);
  const [adding, setAdding] = useState(false);

  const getGroupDetails = async () => {
    try {
      const res = await ExamGroupService.getExamGroupById(groupId);
      if (res?.status === 200) {
        setGroup(res.data.data.data || null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch exam group");
    }
  };

  const getExamsInGroup = async () => {
    setLoading(true);
    try {
      const res = await ExamGroupService.getAllExamsInGroup(groupId);
      if (res?.status === 200) {
        setExams(res.data.data.data || res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllExams = async () => {
    try {
      const res = await ExamServices.getAllExamsLean();
      if (res?.status === 200) {
        setAllExams(res.data.data?.examData || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!groupId) return;
    getGroupDetails();
    getExamsInGroup();
    getAllExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const availableExams = allExams
    .filter((e) => !exams.some((added) => added._id === e._id))
    .map((e) => ({ value: e._id, label: e.examName }));

  const handleAddExam = async () => {
    if (!selectedExam) {
      toast.error("Please select an exam");
      return;
    }
    setAdding(true);
    try {
      const res = await ExamGroupService.addExamToGroup(
        { examId: selectedExam.value },
        groupId
      );
      if (res?.status === 200 || res?.status === 201) {
        toast.success("Exam added to group");
        setSelectedExam(null);
        getExamsInGroup();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add exam");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveExam = async (examId: string) => {
    try {
      const res = await ExamGroupService.removeExamFromGroup(examId);
      if (res?.status === 200) {
        toast.success("Exam removed from group");
        getExamsInGroup();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove exam");
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <span
          className="xrg-back-btn"
          role="button"
          onClick={() => navigate(-1)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </span>
        <div>
          <h5 className="mb-0 fw-bold">{group?.name || "Exam Group"}</h5>
          <span className="text-muted fs-12">Exam group details</span>
        </div>
      </div>

      {/* Info / stat tiles */}
      <Card className="xrg-card shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faLayerGroup} />
                </span>
                <div>
                  <div className="xrg-stat-label">Name</div>
                  <div className="xrg-stat-value">{group?.name || "-"}</div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faCalendar} />
                </span>
                <div>
                  <div className="xrg-stat-label">Created</div>
                  <div className="xrg-stat-value">
                    {group?.createdAt
                      ? new Date(group.createdAt).toLocaleDateString()
                      : "-"}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faIndianRupeeSign} />
                </span>
                <div>
                  <div className="xrg-stat-label">Fees</div>
                  <div className="xrg-stat-value">
                    {group?.fees
                      ? `₹${group.fees.toLocaleString('en-IN')}`
                      : 'Free'}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="xrg-stat">
                <span className="xrg-stat-icon">
                  <FontAwesomeIcon icon={faBook} />
                </span>
                <div>
                  <div className="xrg-stat-label">Total Exams</div>
                  <div className="xrg-stat-value">{exams.length}</div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Exams table */}
      <Card className="xrg-card xrg-table-card shadow-sm">
        <Card.Header className="xrg-card-header">
          <Row className="g-2 align-items-center">
            <Col>
              <span className="fw-bold">Exams in this group</span>
            </Col>
            <Col md={5}>
              <Select
                options={availableExams}
                isClearable
                placeholder="Select an exam to add..."
                value={selectedExam}
                onChange={(e: any) => setSelectedExam(e)}
              />
            </Col>
            <Col xs="auto">
              <Button size="sm" onClick={handleAddExam} disabled={adding}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {adding ? "Adding..." : "Add"}
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="xrg-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 64 }}>#</th>
                <th>Exam Name</th>
                <th className="text-center" style={{ width: 90 }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading exams...
                  </td>
                </tr>
              ) : exams?.length > 0 ? (
                exams.map((exam, index) => (
                  <tr key={exam._id}>
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="xrg-table-icon">
                          <FontAwesomeIcon icon={faBook} />
                        </span>
                        <span className="fw-semibold">
                          {exam.examName || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="text-center">
                      <FontAwesomeIcon
                        icon={faTrash}
                        role="button"
                        title="Remove from group"
                        className="text-danger cursor-pointer px-2"
                        onClick={() => handleRemoveExam(exam._id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center py-5">
                    <span className="xrg-table-icon mx-auto mb-3 d-flex">
                      <FontAwesomeIcon icon={faBook} />
                    </span>
                    <h6 className="fw-bold mb-1">No exams in this group</h6>
                    <p className="text-muted fs-12 mb-0">
                      Use the selector above to add an exam.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}
